import {
    matchAdditionalPatternValidation,
    matchPatternValidation,
    matchPrefix,
} from '@/services/validationSchema.service';
import { TFunction } from 'i18next';

export interface IAPISchemaOptionalProperties {
    disabled?: boolean;
    required?: boolean;
    description?: string;
    placeholder?: string;
    prefix?: string[];
}

export interface ISchemaFieldProperties {
    properties?: {
        [name: string]: ISchemaFieldProperties;
    };
    attr?: IAPISchemaOptionalProperties;
    maxLength: number;
    minLength: number;
    type: string;
    title?: string;
    description?: string;
    widget?: string;
    disabled?: boolean;
    required?: string[];
    items?: ItemsArray;
}

interface ItemsArray {
    type: string;
    enum: string[];
    enum_titles: string[];
}
export interface IAPIFormSchema {
    schema: ISchemaFieldProperties;
    initialValues: {
        [key: string]: string;
    };
}

export interface FieldProperties {
    disabled: boolean;
    placeholder: string;
    description: string;
    label: string;
    additionalFieldProperties: {
        prefix?: string;
        options?: ISchemaOptions[];
    };
}

export interface ISchemaOptions {
    value: string;
    label: string;
}

export interface IValidationNumberProperties {
    value: number;
    message: string;
}

export interface IValidationBooleanProperties {
    value: boolean;
    message: string;
}

export interface FieldValidation {
    minLength?: IValidationNumberProperties;
    maxLength?: IValidationNumberProperties;
    isRequired: IValidationBooleanProperties;
    pattern?: {
        value: RegExp;
        message: string;
    };
    additionalPattern?: {
        value: string;
        message: string;
    };
}

export interface ISchema {
    fieldName: string;
    fieldType: string;
    initialValue: string | boolean;
    properties: FieldProperties;
    validation: FieldValidation;
}

export enum FieldType {
    TEXT = 'text',
    BOOLEAN = 'boolean',
    OBJECT = 'object',
    CHECKBOX = 'checkbox',
    PASSWORD = 'password',
    TEXTAREA = 'textarea',
    STRING = 'string',
    FILE = 'file',
    ARRAY = 'array',
    SELECT = 'select',
    HIDDEN = 'hidden',
    ORIGINAL_DIMENSIONS = 'originalDimensions',
}

export enum FieldName {
    PHONE = 'phone',
    FIRST_NAME = 'firstName',
    SURNAME = 'surname',
    EMAIL = 'email',
    TERMS = 'terms',
    TITLE = 'title',
    ORIGINAL_DIMENSIONS = 'originalDimensions',
}

export enum WidgetName {
    EMAIL = 'email',
    PASSWORD = 'password',
    PHONE = 'phone',
    FILE = 'file',
    HIDDEN = 'hidden',
    TEXTAREA = 'textarea',
}

export const createSchema = (schema: IAPIFormSchema, lang: string, t: TFunction): ISchema[] => {
    const remapedSchema = [];

    if (schema.schema.properties) {
        for (const [fieldName, fieldProperties] of Object.entries(schema.schema.properties)) {
            if (fieldProperties.type === FieldType.OBJECT && fieldProperties.properties) {
                for (const [objectFieldName, objectFieldProperties] of Object.entries(fieldProperties.properties)) {
                    const findRequired = schema.schema.required?.find((item) => item === fieldName);
                    const nestedName = `${fieldName}.${objectFieldName}`;
                    const required = fieldProperties.required?.map((item) => `${findRequired}.${item}`);
                    const title = fieldProperties.title;

                    remapedSchema.push({
                        fieldName: nestedName,
                        fieldType: matchFieldType(objectFieldProperties.type, nestedName, objectFieldProperties.widget),
                        initialValue: setInitialValues(schema, nestedName, objectFieldProperties),
                        properties: matchProperties(objectFieldProperties, nestedName),
                        validation: prepareValidation(objectFieldProperties, nestedName, lang, t, required, title),
                    });
                }
            } else {
                remapedSchema.push({
                    fieldName,
                    nestedFieldName: '',
                    fieldType: matchFieldType(fieldProperties.type, fieldName, fieldProperties.widget),
                    initialValue: setInitialValues(schema, fieldName, fieldProperties),
                    properties: matchProperties(fieldProperties, fieldName),
                    validation: prepareValidation(
                        fieldProperties,
                        fieldName,
                        lang,
                        t,
                        schema.schema.required,
                        fieldProperties.title
                    ),
                });
            }
        }
    }

    return remapedSchema;
};

interface IWidgetStrategy {
    [widgetName: string]: () => FieldType;
}

const matchFieldType = (type: string, fieldName: string, widget?: string) => {
    if (widget) {
        const widgetStrategy: IWidgetStrategy = {
            [WidgetName.FILE]: () => FieldType.FILE,
            [WidgetName.PASSWORD]: () => FieldType.PASSWORD,
            [WidgetName.HIDDEN]: () => FieldType.HIDDEN,
            [WidgetName.TEXTAREA]: () => FieldType.TEXTAREA,
        };

        if (widgetStrategy[widget]) {
            return widgetStrategy[widget]();
        }
    }

    if (type === FieldType.BOOLEAN) {
        return FieldType.CHECKBOX;
    }

    return type;
};

const matchProperties = (fieldProperties: ISchemaFieldProperties, fieldName: string) => {
    return {
        placeholder: fieldProperties.attr?.placeholder || '',
        label: fieldProperties.title || '',
        description: fieldProperties.attr?.description || '',
        disabled: fieldProperties.disabled || false,
        additionalFieldProperties: {
            prefix: matchPrefix(fieldName, fieldProperties.attr?.prefix),
            options: matchOptions(fieldProperties.items),
        },
    };
};

const setInitialValues = (schema: IAPIFormSchema, fieldName: string, properties: ISchemaFieldProperties) => {
    const initialValue = schema.initialValues[fieldName] || '';

    if (fieldName === FieldName.PHONE) {
        const prefix = matchPrefix(fieldName, properties.attr?.prefix);

        return initialValue.length ? initialValue : prefix || '';
    }

    return initialValue;
};

const prepareValidation = (
    fieldProperties: ISchemaFieldProperties,
    fieldName: string,
    lang: string,
    t: TFunction,
    required?: string[],
    title?: string
) => {
    return {
        minLength: {
            value: fieldProperties.minLength,
            message: t('translations.forms.error.minLength', { value: fieldProperties.minLength }),
        },
        maxLength: {
            value: fieldProperties.maxLength,
            message: t('translations.forms.error.maxLength', { value: fieldProperties.maxLength, name: title }),
        },
        isRequired: {
            value: Boolean(required?.some((item) => item === fieldName)),
            message: t('translations.forms.error.required'),
        },
        pattern: matchPatternValidation(fieldName, lang, t, fieldProperties.widget),
        additionalPattern: matchAdditionalPatternValidation(fieldName, t, fieldProperties.widget),
    };
};

const matchOptions = (items?: ItemsArray) => {
    if (!items) {
        return;
    }

    return items.enum.map((item, index) => ({
        value: item,
        label: items.enum_titles[index],
    }));
};
